import React, { useEffect, useState } from 'react';
import { Button, Card, Col, Divider, Dropdown, Empty, Menu, message, Modal, Row } from 'antd';
import { PlusCircleOutlined } from '@ant-design/icons';
import * as QueryString from 'query-string';
import { useLocation } from 'react-router';
import styled from 'styled-components';
import {
    useCreateSecretMutation,
    useDeleteSecretMutation,
    useListSecretsQuery,
} from '../../../graphql/ingestion.generated';
import { SecretBuilderModal } from './SecretBuilderModal';
import { SecretBuilderState } from './types';
import { StyledTable } from '../../entity/shared/components/styled/StyledTable';
import { SearchBar } from '../../search/SearchBar';
import { useEntityRegistry } from '../../useEntityRegistry';
import { scrollToTop } from '../../shared/searchUtils';
import { addSecretToListSecretsCache, removeSecretFromListSecretsCache } from './cacheUtils';
import { useTranslation } from '../../../i18n/useTranslation';
import { ReactComponent as IconMoreVert } from '../../../images/icon-more-vert.svg';
import { PaginationBar } from '../../entity/shared/tabs/Glossary/PaginationBar';
import { ComponentSpinner } from '../../shared/ComponentSpinner';
import { AutoTooltip } from '../../entity/shared/components/common/AutoTooltip';

const DeleteButtonContainer = styled.div`
    display: flex;
    justify-content: right;
`;

const TableActionHeader = styled.div`
    text-align: right;
`;

const DEFAULT_PAGE_SIZE = 10;

export const SecretsList = () => {
    const { t } = useTranslation();
    const entityRegistry = useEntityRegistry();
    const location = useLocation();
    const params = QueryString.parse(location.search, { arrayFormat: 'comma' });
    const paramsQuery = (params?.query as string) || undefined;
    const [query, setQuery] = useState<undefined | string>(undefined);
    useEffect(() => setQuery(paramsQuery), [paramsQuery]);

    const [page, setPage] = useState(1);

    const pageSize = DEFAULT_PAGE_SIZE;
    const start = (page - 1) * pageSize;

    // Whether or not there is an urn to show in the modal
    const [isCreatingSecret, setIsCreatingSecret] = useState<boolean>(false);

    const [deleteSecretMutation] = useDeleteSecretMutation();
    const [createSecretMutation] = useCreateSecretMutation();
    const { loading, error, data, client } = useListSecretsQuery({
        variables: {
            input: {
                start,
                count: pageSize,
                query: query && query.length > 0 ? query : undefined,
            },
        },
        fetchPolicy: query && query.length > 0 ? 'no-cache' : 'cache-first',
    });

    const totalSecrets = data?.listSecrets?.total || 0;
    const secrets = data?.listSecrets?.secrets || [];

    const deleteSecret = (urn: string) => {
        deleteSecretMutation({
            variables: { urn },
        })
            .then(() => {
                message.success({ content: t('Removed secret.'), duration: 2 });
                removeSecretFromListSecretsCache(urn, client, page, pageSize);
            })
            .catch((e: unknown) => {
                message.destroy();
                if (e instanceof Error) {
                    message.error({ content: `${t('Failed to remove secret:')} \n ${e.message || ''}`, duration: 3 });
                }
            });
    };

    const onChangePage = (newPage: number) => {
        scrollToTop();
        setPage(newPage);
    };

    const onSubmit = (state: SecretBuilderState, resetBuilderState: () => void) => {
        createSecretMutation({
            variables: {
                input: {
                    name: state.name as string,
                    value: state.value as string,
                    description: state.description as string,
                },
            },
        })
            .then((res) => {
                message.success({
                    content: t('Successfully created Secret!'),
                    duration: 3,
                });
                resetBuilderState();
                setIsCreatingSecret(false);
                addSecretToListSecretsCache(
                    {
                        urn: res.data?.createSecret || '',
                        name: state.name,
                        description: state.description,
                    },
                    client,
                    pageSize,
                );
            })
            .catch((e) => {
                message.destroy();
                message.error({
                    content: `${t('Failed to update ingestion source!')} \n ${e.message || ''}`,
                    duration: 3,
                });
            });
    };

    const onDeleteSecret = (urn: string) => {
        Modal.confirm({
            title: t('Confirm Secret Removal'),
            content: t('ARE_YOU_SURE_REMOVE_SECRET_DESC'),
            onOk() {
                deleteSecret(urn);
            },
            onCancel() {},
            okText: t('Yes'),
            cancelText: t('Cancel'),
            okType: 'danger',
            maskClosable: true,
            centered: true,
        });
    };

    const tableColumns = [
        {
            width: '25%',
            title: t('Name'),
            dataIndex: 'name',
            key: 'name',
            ellipsis: { showTitle: false },
            render: (name: string) => (
                <AutoTooltip className="f-text-medium-content f-color-dark-black-s100">{name}</AutoTooltip>
            ),
        },
        {
            width: '70%',
            title: t('Description'),
            dataIndex: 'description',
            key: 'description',
            ellipsis: { showTitle: false },
            render: (description: any) => {
                return (
                    <AutoTooltip
                        className={`f-text-medium-content ${
                            description ? 'f-text-medium-content f-color-dark-black-s100' : 'f-color-dark-black-s70'
                        }`}
                    >
                        {description || t('No description')}
                    </AutoTooltip>
                );
            },
        },
        {
            title: <TableActionHeader>{t('Actions')}</TableActionHeader>,
            dataIndex: '',
            width: '5%',
            key: 'x',
            render: (_, record: any) => (
                <DeleteButtonContainer>
                    <Dropdown
                        trigger={['click']}
                        placement="bottomRight"
                        overlay={
                            <Menu className="f-dropdown-small">
                                <Menu.Item onClick={() => onDeleteSecret(record.urn)}>{t('Delete')}</Menu.Item>
                            </Menu>
                        }
                    >
                        <Button
                            type="text"
                            title={t('More Actions')}
                            icon={<IconMoreVert />}
                            aria-label={t('More Actions')}
                            className="icon-button ma-0"
                        />
                    </Dropdown>
                </DeleteButtonContainer>
            ),
        },
    ];

    const tableData = secrets?.map((secret) => ({
        urn: secret.urn,
        name: secret.name,
        description: secret.description,
    }));

    return (
        <>
            {error && message.error({ content: `Failed to load secrets! \n ${error.message || ''}`, duration: 3 })}
            <Card className="f-rounded-corner-1 pa-0 overflow-hidden">
                <Row className="f-gap-3 px-3 py-2" align="middle">
                    <Col flex="auto">
                        <SearchBar
                            suggestions={[]}
                            hideRecommendations
                            onSearch={() => null}
                            initialQuery={query || ''}
                            entityRegistry={entityRegistry}
                            placeholderText={t('Search secrets')}
                            inputStyle={{ height: 32, fontSize: 14 }}
                            onQueryChange={(q) => {
                                setQuery(q);
                                setPage(1);
                            }}
                        />
                    </Col>
                    <Col flex="none">
                        <Button
                            type="text"
                            className="f-btn-flat"
                            aria-label={t('Create new secret')}
                            data-testid="create-secret-button"
                            onClick={() => setIsCreatingSecret(true)}
                        >
                            <PlusCircleOutlined aria-hidden="true" />
                            <span>{t('Create new secret')}</span>
                        </Button>
                    </Col>
                </Row>
                <Divider className="ma-0" />
                <div className="f-info-cards">
                    <div>
                        <div className="f-ic-title">{t('Secrets')}</div>
                        <div className="f-ic-content">
                            <div className="f-ic-value">{totalSecrets}</div>
                        </div>
                    </div>
                </div>
                {loading && <ComponentSpinner />}
                <Divider className="ma-0" />
                <StyledTable
                    rowKey="urn"
                    columns={tableColumns}
                    dataSource={tableData}
                    locale={{
                        emptyText: <Empty description={t('No Secrets found!')} image={Empty.PRESENTED_IMAGE_SIMPLE} />,
                    }}
                    pagination={false}
                />
                <Divider className="ma-0" />
                <PaginationBar
                    page={page}
                    pageSize={pageSize}
                    loading={loading}
                    pageSizeOptions={[]}
                    totalCount={totalSecrets}
                    onChangePage={onChangePage}
                    onShowSizeChange={() => {}}
                />
            </Card>
            {isCreatingSecret && (
                <SecretBuilderModal
                    visible={isCreatingSecret}
                    onSubmit={onSubmit}
                    onCancel={() => setIsCreatingSecret(false)}
                />
            )}
        </>
    );
};
